/**
 * Handles PDP variant hover gallery functionality.
 * Temporarily swaps the main gallery images and thumbnails when hovering over variant swatches.
 */
class VariantHover {
	constructor() {
		this.mql = window.matchMedia('(min-width: 990px)'); // Focus on desktop as requested
		if (!this.mql.matches) return;

		this.selectors = {
			variantItem: '.js-custom-product-variant',
			galleryList: '[id^="Slider-Gallery-"]',
			thumbnailsList: '[id^="Slider-Thumbnails-"]',
			viewerSlider: 'slider-component[id^="GalleryViewer-"]',
			thumbnailSlider: 'slider-component[id^="GalleryThumbnails-"]',
			galleryCounter: '.slider-counter--total'
		};

		this.variantItems = document.querySelectorAll(this.selectors.variantItem);
		this.galleryList = document.querySelector(this.selectors.galleryList);
		this.thumbnailsList = document.querySelector(this.selectors.thumbnailsList);
		this.viewerSlider = document.querySelector(this.selectors.viewerSlider);
		this.thumbnailSlider = document.querySelector(this.selectors.thumbnailSlider);
		this.galleryCounters = document.querySelectorAll(this.selectors.galleryCounter);

		if (!this.variantItems.length || !this.galleryList) return;

		this.originalContent = {
			gallery: this.galleryList.innerHTML,
			thumbnails: this.thumbnailsList ? this.thumbnailsList.innerHTML : null,
			count: this.galleryCounters.length > 0 ? this.galleryCounters[0].textContent : null
		};

		this.isHovering = false;
		this.currentTimeout = null;

		this.init();
	}

	init() {
		this.variantItems.forEach(item => {
			item.addEventListener('mouseenter', (e) => this.onMouseEnter(e));
			item.addEventListener('mouseleave', (e) => this.onMouseLeave(e));
			item.addEventListener('click', (e) => this.onClick(e));
		});

		// Listen for window resize to enable/disable on the fly if needed
		window.addEventListener('resize', () => {
			if (!this.mql.matches) {
				this.restoreGallery();
			}
		});
	}

	onClick(event) {
		if (!this.mql.matches) return;

		const item = event.currentTarget;
		const hoverMedia = item.dataset.hoverMedia;

		if (!hoverMedia) return;

		if (this.currentTimeout) clearTimeout(this.currentTimeout);

		this.isHovering = true;
		const mediaUrls = hoverMedia.split(',').filter(url => url.trim() !== '');

		if (mediaUrls.length > 0) {
			// Preload the first image to minimize delay
			const firstImage = new Image();
			firstImage.src = mediaUrls[0];
			firstImage.onload = () => {
				if (this.isHovering) {
					this.transitionGallery(mediaUrls);
				}
			};
		}
	}

	onMouseEnter(event) {
		if (!this.mql.matches) return;

		const item = event.currentTarget;
		const hoverMedia = item.dataset.hoverMedia;

		if (!hoverMedia) return;

		if (this.currentTimeout) clearTimeout(this.currentTimeout);

		this.isHovering = true;
		const mediaUrls = hoverMedia.split(',').filter(url => url.trim() !== '');

		if (mediaUrls.length > 0) {
			// Preload the first image to minimize delay
			const firstImage = new Image();
			firstImage.src = mediaUrls[0];
			firstImage.onload = () => {
				if (this.isHovering) {
					this.transitionGallery(mediaUrls);
				}
			};
		}
	}

	onMouseLeave(event) {
		if (!this.mql.matches) return;

		this.isHovering = false;

		this.currentTimeout = setTimeout(() => {
			if (!this.isHovering) {
				this.restoreGallery();
			}
		}, 50);
	}

	onClick(event) {
		this.isHovering = true;
	}

	transitionGallery(urls) {
		// 1. Lock the container height to prevent jumping
		if (this.galleryList) {
			this.galleryList.style.height = `${this.galleryList.offsetHeight}px`;
			this.galleryList.classList.add('is-fading-out');
		}

		// 2. Wait for fade out
		setTimeout(() => {
			if (!this.isHovering) {
				// Abort if user left mid-transition
				this.galleryList.classList.remove('is-fading-out');
				this.galleryList.style.height = '';
				return;
			}

			// Capture current active slide dimensions to replicate
			const activeSlide = this.galleryList.querySelector('.slider__slide.is-active .product-media-container');
			let fixedHeight = '100%';
			let fixedRatio = 1.0;

			if (activeSlide) {
				fixedHeight = `${activeSlide.offsetHeight}px`;
				// try to get ratio from style
				const ratioMatch = activeSlide.getAttribute('style').match(/--ratio:\s*([\d\.]+)/);
				if (ratioMatch) {
					fixedRatio = ratioMatch[1];
				}
			}

			this.updateGallery(urls, fixedHeight, fixedRatio);

			// 3. Fade in
			requestAnimationFrame(() => {
				this.galleryList.classList.remove('is-fading-out');
				// Remove height lock after transition is done (optional, but safer to keep if images vary)
				// But if we want to support responsive resizing, we should eventually remove it.
				// For now, let's keep it locked only during the hover session or until swap is done.
				setTimeout(() => {
					this.galleryList.style.height = '';
				}, 300);
			});
		}, 200); // slightly faster than CSS to feel snappy
	}

	updateGallery(urls, fixedHeight = '100%', fixedRatio = 1.0) {
		let mainHtml = '';
		let thumbHtml = '';

		urls.forEach((url, index) => {
			// Main Gallery LI
			// We force the container to have the same height as the previous one
			mainHtml += `
        <li id="Slide-Hover-${index}" class="product__media-item grid__item slider__slide ${index === 0 ? 'is-active' : ''} product__media-item--hover-swap" data-media-id="hover-${index}">
          <div class="product-media-container media-type-image media-fit-cover global-media-settings gradient" style="--ratio: ${fixedRatio}; height: ${fixedHeight};">
            <div class="product__media media media--transparent" style="padding-bottom: 0; height: 100%;">
              <img src="${url}" loading="${index === 0 ? 'eager' : 'lazy'}" class="image-magnify-none" style="object-fit: cover; height: 100%; width: 100%;">
            </div>
          </div>
        </li>
      `;

			// Thumbnail LI
			if (this.thumbnailsList) {
				thumbHtml += `
          <li id="Slide-Thumbnails-Hover-${index}" class="thumbnail-list__item slider__slide" data-target="hover-${index}">
            <button class="thumbnail global-media-settings global-media-settings--no-shadow" ${index === 0 ? 'aria-current="true"' : ''}>
              <img src="${url}" loading="lazy" style="object-fit: cover; width: 100%; height: 100%;">
            </button>
          </li>
        `;
			}
		});

		this.galleryList.innerHTML = mainHtml;
		if (this.thumbnailsList) {
			this.thumbnailsList.innerHTML = thumbHtml;
		}

		// Update counters
		this.galleryCounters.forEach(counter => {
			counter.textContent = urls.length;
		});

		this.reinitSliders();
	}

	restoreGallery() {
		if (this.galleryList.innerHTML === this.originalContent.gallery) return;

		// Apply fade out
		this.galleryList.classList.add('is-fading-out');
		this.galleryList.style.height = `${this.galleryList.offsetHeight}px`;

		setTimeout(() => {
			this.galleryList.innerHTML = this.originalContent.gallery;
			if (this.thumbnailsList && this.originalContent.thumbnails) {
				this.thumbnailsList.innerHTML = this.originalContent.thumbnails;
			}

			if (this.originalContent.count) {
				this.galleryCounters.forEach(counter => {
					counter.textContent = this.originalContent.count;
				});
			}

			this.reinitSliders();

			// Remove fade out
			requestAnimationFrame(() => {
				this.galleryList.classList.remove('is-fading-out');
				setTimeout(() => {
					this.galleryList.style.height = '';
				}, 300);
			});
		}, 200);
	}

	reinitSliders() {
		if (this.viewerSlider && this.viewerSlider.resetPages) {
			this.viewerSlider.resetPages();
			this.viewerSlider.scrollTo({ left: 0 });
		}
		if (this.thumbnailSlider && this.thumbnailSlider.resetPages) {
			this.thumbnailSlider.resetPages();
			this.thumbnailSlider.scrollTo({ left: 0 });
		}
	}
}

document.addEventListener('DOMContentLoaded', () => {
	new VariantHover();
});
